<template>
  <div class="reset-password-page">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">パスワードリセット</h1>
      </div>
    </div>

    <section class="section">
      <div class="container">
        <div class="reset-container">
          
          <!-- 重置密码表单 -->
          <form @submit.prevent="handleResetPassword" class="reset-form">
            <h2 class="form-title">新しいパスワードを設定</h2>
            <p class="reset-info">
              新しいパスワードを入力してください。
            </p>

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
            <div v-if="successMessage" class="success-message">
              {{ successMessage }}
            </div>

            <div class="form-group password-group">
              <label for="new-password">新しいパスワード</label>
              <input
                :type="passwordFieldType"
                id="new-password"
                v-model="newPassword"
                required
                placeholder="••••••••"
                minlength="6"
              >
              <button type="button" @click="togglePasswordVisibility" class="toggle-password">
                <i :class="['fas', passwordFieldType === 'password' ? 'fa-eye' : 'fa-eye-slash']"></i>
              </button>
            </div>

            <div class="form-group password-group">
              <label for="confirm-password">パスワード確認</label>
              <input
                :type="confirmPasswordFieldType"
                id="confirm-password"
                v-model="confirmPassword"
                required
                placeholder="••••••••"
                minlength="6"
              >
              <button type="button" @click="toggleConfirmPasswordVisibility" class="toggle-password">
                <i :class="['fas', confirmPasswordFieldType === 'password' ? 'fa-eye' : 'fa-eye-slash']"></i>
              </button>
            </div>

            <button type="submit" class="btn btn-primary" :disabled="isLoading || !isValidForm">
              <span v-if="isLoading">処理中...</span>
              <span v-else>パスワードをリセット</span>
            </button>

            <div class="auth-links">
              <router-link to="/auth" class="btn-link">
                ログイン画面に戻る
              </router-link>
            </div>
          </form>

        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { authService } from '@/api';

const router = useRouter();
const route = useRoute();

// 表单数据
const newPassword = ref('');
const confirmPassword = ref('');
const token = ref('');

// 状态管理
const isLoading = ref(false);
const error = ref('');
const successMessage = ref('');
const passwordFieldType = ref('password');
const confirmPasswordFieldType = ref('password');

// 计算属性
const isValidForm = computed(() => {
  return newPassword.value.length >= 6 && 
         confirmPassword.value.length >= 6 && 
         newPassword.value === confirmPassword.value;
});

// 密码可见性切换
const togglePasswordVisibility = () => {
  passwordFieldType.value = passwordFieldType.value === 'password' ? 'text' : 'password';
};

const toggleConfirmPasswordVisibility = () => {
  confirmPasswordFieldType.value = confirmPasswordFieldType.value === 'password' ? 'text' : 'password';
};

// 清除消息
const clearMessages = () => {
  error.value = '';
  successMessage.value = '';
};

// 重置密码处理
const handleResetPassword = async () => {
  clearMessages();
  
  if (!newPassword.value || newPassword.value.length < 6) {
    error.value = 'パスワードは6文字以上で入力してください。';
    return;
  }
  
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'パスワードが一致しません。';
    return;
  }
  
  if (!token.value) {
    error.value = '無効なリセットトークンです。';
    return;
  }

  isLoading.value = true;
  
  try {
    await authService.resetPassword(token.value, newPassword.value);
    
    successMessage.value = 'パスワードが正常にリセットされました！ログイン画面に移動します...';
    
    setTimeout(() => {
      router.push('/auth');
    }, 2000);
    
  } catch (error) {
    console.error('Reset password error:', error);
    error.value = error.message || 'パスワードリセットに失敗しました。';
  } finally {
    isLoading.value = false;
  }
};

// 组件挂载时获取token
onMounted(() => {
  token.value = route.query.token;
  
  if (!token.value) {
    error.value = 'リセットトークンが見つかりません。';
  }
});
</script>

<style scoped>
.reset-password-page {
  min-height: calc(100vh - 80px);
}

.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 50px 0;
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
}

.reset-container {
  max-width: 450px;
  margin: 0 auto;
  padding: 0 15px;
}

.reset-form {
  background-color: #fff;
  padding: 30px 35px;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
}

.form-title {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
  font-size: 20px;
  font-weight: 600;
}

.reset-info {
  text-align: center;
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(255, 143, 171, 0.2);
}

.password-group {
  position: relative;
}

.toggle-password {
  position: absolute;
  top: 68%;
  right: 12px;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  padding: 5px;
}

.toggle-password:hover {
  color: #555;
}

.toggle-password i {
  font-size: 16px;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.success-message {
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.btn {
  display: inline-block;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  border: none;
  font-size: 16px;
  width: 100%;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--secondary-color);
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.auth-links {
  margin-top: 25px;
  text-align: center;
}

.btn-link {
  color: var(--primary-color);
  text-decoration: underline;
  font-size: 14px;
}

.btn-link:hover {
  color: var(--secondary-color);
}
</style> 